import { useState, useEffect, useMemo, useRef } from 'react';
import DashboardLayout from '../../components/feature/DashboardLayout';
import { useAuth } from '../../lib/api/auth';
import { api } from '../../lib/api';
import { useFamilyData } from '../../lib/context/FamilyDataContext';
import type { ChildInfo, BankAccountInfo, BankTransactionInfo } from '../../lib/api/types';

const txTypeConfig: Record<number, { icon: string; color: string; bg: string; label: string }> = {
  1: { icon: 'ri-arrow-down-circle-fill', color: 'text-emerald-600', bg: 'bg-emerald-50', label: '存入' },
  2: { icon: 'ri-arrow-up-circle-fill', color: 'text-rose-500', bg: 'bg-rose-50', label: '取出' },
  3: { icon: 'ri-percent-fill', color: 'text-blue-600', bg: 'bg-blue-50', label: '利息' },
  4: { icon: 'ri-coin-fill', color: 'text-cyan-600', bg: 'bg-cyan-50', label: '充值积分' },
  5: { icon: 'ri-exchange-fill', color: 'text-amber-600', bg: 'bg-amber-50', label: '积分换钱' }
};

const categoryConfig: Record<string, { icon: string; label: string; color: string }> = {
  hongbao: { icon: 'ri-red-packet-fill', label: '红包', color: 'text-red-500' },
  allowance: { icon: 'ri-wallet-fill', label: '零花钱', color: 'text-blue-500' },
  achievement: { icon: 'ri-trophy-fill', label: '成就奖励', color: 'text-amber-500' },
  chores: { icon: 'ri-tools-fill', label: '家务报酬', color: 'text-emerald-500' },
  sell: { icon: 'ri-price-tag-3-fill', label: '出售', color: 'text-violet-500' },
  other: { icon: 'ri-more-fill', label: '其他', color: 'text-gray-500' },
  exchange: { icon: 'ri-exchange-cny-fill', label: '积分兑换', color: 'text-orange-500' },
  spend: { icon: 'ri-shopping-bag-fill', label: '消费', color: 'text-rose-500' },
  snack: { icon: 'ri-cake-3-fill', label: '零食', color: 'text-pink-500' },
  stationery: { icon: 'ri-pen-nib-fill', label: '文具', color: 'text-indigo-500' },
  interest: { icon: 'ri-bank-fill', label: '利息收益', color: 'text-blue-600' }
};

const depositQuickOptions = [
  { key: 'hongbao', label: '红包', icon: 'ri-red-packet-fill', color: 'bg-red-50 text-red-500 border-red-100' },
  { key: 'allowance', label: '零花钱', icon: 'ri-wallet-fill', color: 'bg-blue-50 text-blue-500 border-blue-100' },
  { key: 'achievement', label: '成就奖励', icon: 'ri-trophy-fill', color: 'bg-amber-50 text-amber-500 border-amber-100' },
  { key: 'chores', label: '家务报酬', icon: 'ri-tools-fill', color: 'bg-emerald-50 text-emerald-500 border-emerald-100' },
  { key: 'sell', label: '出售', icon: 'ri-price-tag-3-fill', color: 'bg-violet-50 text-violet-500 border-violet-100' },
  { key: 'other', label: '其他', icon: 'ri-more-fill', color: 'bg-gray-50 text-gray-500 border-gray-200' }
];

const withdrawQuickOptions = [
  { key: 'spend', label: '日常消费', icon: 'ri-shopping-bag-fill', color: 'bg-rose-50 text-rose-500 border-rose-100' },
  { key: 'snack', label: '零食', icon: 'ri-cake-3-fill', color: 'bg-pink-50 text-pink-500 border-pink-100' },
  { key: 'stationery', label: '文具', icon: 'ri-pen-nib-fill', color: 'bg-indigo-50 text-indigo-500 border-indigo-100' },
  { key: 'exchange', label: '积分兑换', icon: 'ri-exchange-cny-fill', color: 'bg-orange-50 text-orange-500 border-orange-100' },
  { key: 'other', label: '其他', icon: 'ri-more-fill', color: 'bg-gray-50 text-gray-500 border-gray-200' }
];

export default function BankPage() {
  const { currentFamily } = useAuth();
  const { children: apiChildren } = useFamilyData(); // 从共享Context获取
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Record<string, BankAccountInfo>>({});
  const [transactions, setTransactions] = useState<BankTransactionInfo[]>([]);
  const [activeChildId, setActiveChildId] = useState<string>('');
  const [interestRate, setInterestRate] = useState(3.0);

  // Modal states
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);

  // Form states
  const [depositForm, setDepositForm] = useState({ amount: '', source_type: 'other', note: '' });
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', source_type: 'other', note: '' });
  const [rechargeForm, setRechargeForm] = useState({ amount: '' });
  const [exchangeForm, setExchangeForm] = useState({ points: '' });
  const [rateForm, setRateForm] = useState({ rate: '3.0' });

  // Transaction filters
  const [txTimeFilter, setTxTimeFilter] = useState<'all' | '7d' | '30d' | '90d'>('all');
  const [txFlowFilter, setTxFlowFilter] = useState<'all' | 'in' | 'out'>('all');

  // 积分兑换比例（从家庭设置获取）
  const exchangeRatio = Math.round(currentFamily?.exchange_ratio || 10); // 1元 = N积分（整数）

  // 使用共享Context的children
  const children = apiChildren;
  const loadedFamilyRef = useRef<string | null>(null);

  // 加载银行账户数据（仅在家庭切换时加载，避免重复）
  useEffect(() => {
    if (!currentFamily?.id || children.length === 0) {
      setLoading(false);
      return;
    }
    if (loadedFamilyRef.current === currentFamily.id) return;
    loadedFamilyRef.current = currentFamily.id;
    loadData();
  }, [currentFamily?.id, children]);

  const loadData = async () => {
    if (!currentFamily?.id || children.length === 0) return;
    setLoading(true);
    try {
      // 设置默认活跃儿童
      if (!activeChildId && children.length > 0) {
        setActiveChildId(children[0].id);
      }

      // 加载所有儿童的银行账户数据
      const accountsMap: Record<string, BankAccountInfo> = {};
      await Promise.all(
        children.map(async (child) => {
          try {
            const accountRes = await api.bank.getBankAccount(child.id);
            if (accountRes.code === 0 && accountRes.data) {
              accountsMap[child.id] = accountRes.data;
            }
          } catch (error) {
            console.error(`Failed to load account for child ${child.id}:`, error);
          }
        })
      );
      setAccounts(accountsMap);

      // 获取利率
      const rateRes = await api.bank.getInterestRate(currentFamily.id);
      if (rateRes.code === 0 && rateRes.data) {
        setInterestRate(rateRes.data.annual_rate);
        setRateForm({ rate: String(rateRes.data.annual_rate) });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 加载交易记录（银行账户已在loadData中获取）
  const loadedTxChildRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeChildId) return;
    if (loadedTxChildRef.current === activeChildId) return;
    loadedTxChildRef.current = activeChildId;
    loadTransactions();
  }, [activeChildId]);

  const loadTransactions = async () => {
    if (!activeChildId) return;
    try {
      // 只获取交易记录（银行账户已在loadData中获取）

      const txRes = await api.bank.getTransactions(activeChildId);
      if (txRes.code === 0 && txRes.data) {
        setTransactions(txRes.data);
      }
    } catch (error) {
      console.error('Failed to load child bank data:', error);
    }
  };

  const activeChild = children.find(c => c.id === activeChildId);
  const activeAccount = accounts[activeChildId];

  const totalIn = transactions.filter(t => t.transaction_type === 1 || t.transaction_type === 3 || t.transaction_type === 4)
    .reduce((a, t) => a + Number(t.amount), 0);
  const totalOut = transactions.filter(t => t.transaction_type === 2 || t.transaction_type === 5)
    .reduce((a, t) => a + Math.abs(Number(t.amount)), 0);

  const monthlyRate = interestRate / 100 / 12;
  const nextMonthInterest = activeAccount ? Number(activeAccount.balance) * monthlyRate : 0;
  const rechargePoints = rechargeForm.amount ? Math.floor(Number(rechargeForm.amount) * exchangeRatio) : 0;
  const exchangeRmb = exchangeForm.points ? (Number(exchangeForm.points) / exchangeRatio).toFixed(2) : '0.00';

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    let list = transactions;
    const now = new Date();
    if (txTimeFilter !== 'all') {
      const days = txTimeFilter === '7d' ? 7 : txTimeFilter === '30d' ? 30 : 90;
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      list = list.filter(t => new Date(t.created_at) >= cutoff);
    }
    if (txFlowFilter === 'in') list = list.filter(t => Number(t.amount) > 0);
    if (txFlowFilter === 'out') list = list.filter(t => Number(t.amount) < 0);
    return list;
  }, [transactions, txTimeFilter, txFlowFilter]);

  // 处理存入
  const handleDeposit = async () => {
    if (!activeChildId || !depositForm.amount) return;
    try {
      const res = await api.bank.deposit(activeChildId, {
        amount: Number(depositForm.amount),
        source_type: depositForm.source_type,
        remark: depositForm.note
      });
      if (res.code === 0) {
        setShowDepositModal(false);
        setDepositForm({ amount: '', source_type: 'other', note: '' });
        loadData();
        loadTransactions();
      }
    } catch (error) {
      console.error('Deposit failed:', error);
      alert('存入失败，请重试');
    }
  };

  // 处理取出
  const handleWithdraw = async () => {
    if (!activeChildId || !withdrawForm.amount) return;
    try {
      const res = await api.bank.withdraw(activeChildId, {
        amount: Number(withdrawForm.amount),
        source_type: withdrawForm.source_type,
        remark: withdrawForm.note
      });
      if (res.code === 0) {
        setShowWithdrawModal(false);
        setWithdrawForm({ amount: '', source_type: 'other', note: '' });
        loadData();
        loadTransactions();
      }
    } catch (error: any) {
      console.error('Withdraw failed:', error);
      alert(error?.response?.data?.detail || '取出失败，请重试');
    }
  };

  // 处理充值积分
  const handleRecharge = async () => {
    if (!activeChildId || !rechargeForm.amount) return;
    try {
      const res = await api.bank.rechargePoints(activeChildId, {
        amount: Number(rechargeForm.amount)
      });
      if (res.code === 0) {
        setShowRechargeModal(false);
        setRechargeForm({ amount: '' });
        loadData();
        loadTransactions();
      }
    } catch (error: any) {
      console.error('Recharge failed:', error);
      alert(error?.response?.data?.detail || '充值失败，请重试');
    }
  };

  // 处理积分换钱
  const handleExchange = async () => {
    if (!activeChildId || !exchangeForm.points) return;
    try {
      const res = await api.bank.exchangePointsToMoney(activeChildId, {
        points: Number(exchangeForm.points)
      });
      if (res.code === 0) {
        setShowExchangeModal(false);
        setExchangeForm({ points: '' });
        loadData();
        loadTransactions();
      }
    } catch (error: any) {
      console.error('Exchange failed:', error);
      alert(error?.response?.data?.detail || '兑换失败，请重试');
    }
  };

  // 处理利率设置
  const handleSetRate = async () => {
    if (!currentFamily?.id) return;
    try {
      const res = await api.bank.setInterestRate(currentFamily.id, Number(rateForm.rate));
      if (res.code === 0) {
        setShowRateModal(false);
        setInterestRate(Number(rateForm.rate));
      }
    } catch (error) {
      console.error('Set rate failed:', error);
      alert('设置利率失败，请重试');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-blue-500 mb-3"></i>
            <p className="text-gray-400">加载中...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (children.length === 0) {
    return (
      <DashboardLayout>
        <div className="p-4 md:p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-blue-50 rounded-2xl mx-auto mb-4">
              <i className="ri-bank-line text-3xl text-blue-300"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-700 mb-2">暂无儿童，银行功能不启用</h3>
            <p className="text-gray-400 text-sm">请先在成员管理中添加儿童</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-black text-blue-900 flex items-center gap-2">
              <i className="ri-bank-fill text-blue-500"></i>家庭银行
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">零花钱管理 · 按月结息</p>
          </div>
          <button onClick={() => {
            if (currentFamily?.my_role !== 1) {
              alert('记录员无权修改利率设置，仅管理员可操作');
              return;
            }
            setShowRateModal(true);
          }}
            className={`flex items-center gap-2 px-4 py-2 bg-white border rounded-xl text-sm transition-colors whitespace-nowrap ${
              currentFamily?.my_role === 1
                ? 'border-blue-100 text-blue-600 hover:border-blue-400 cursor-pointer'
                : 'border-gray-100 text-gray-400 cursor-not-allowed'
            }`}>
            <i className="ri-settings-3-fill text-sm"></i>利率设置
            {currentFamily?.my_role !== 1 && <span className="text-[10px] text-gray-400 ml-1">管理员</span>}
          </button>
        </div>

        {/* Child Switcher */}
        <div className="flex gap-3 mb-5 flex-wrap">
          {children.map(child => {
            const acc = accounts[child.id];
            return (
              <button key={child.id} onClick={() => setActiveChildId(child.id)}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                  activeChildId === child.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-200'
                }`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                  style={{ background: child.avatar_color || '#3B82F6' }}>
                  {(child.nickname || child.name || '?').charAt(0)}
                </div>
                <div className="text-left">
                  <div className={`text-sm font-bold ${activeChildId === child.id ? 'text-blue-600' : 'text-blue-900'}`}>{child.name}</div>
                  <div className="text-xs text-gray-400">¥ {acc ? Number(acc.balance).toFixed(2) : '0.00'}</div>
                </div>
              </button>
            );
          })}
        </div>

        {activeChild && activeAccount && (
          <>
            {/* Main Balance Card */}
            <div className="rounded-2xl p-6 mb-5 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 50%, #1E88E5 100%)' }}>
              <div className="absolute -right-8 -top-8 w-44 h-44 bg-white/5 rounded-full"></div>
              <div className="absolute right-16 -bottom-10 w-28 h-28 bg-yellow-400/10 rounded-full"></div>
              <div className="flex items-start justify-between relative z-10 mb-4">
                <div>
                  <p className="text-white/60 text-sm mb-1">{activeChild.name}的家庭银行</p>
                  <div className="flex items-end gap-2">
                    <span className="text-white/60 text-lg font-medium">¥</span>
                    <span className="text-5xl font-black text-white">{Number(activeAccount.balance).toFixed(2)}</span>
                  </div>
                  <p className="text-white/50 text-xs mt-1">账户总余额</p>
                </div>
                <div className="bg-white/10 rounded-xl p-3 text-right">
                  <p className="text-white/50 text-xs mb-0.5">年化利率</p>
                  <p className="text-white font-black text-xl">{interestRate}%</p>
                  <p className="text-white/40 text-[10px]">按月结息</p>
                </div>
              </div>
              <div className="flex gap-4 pt-4 border-t border-white/10 relative z-10 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-emerald-400/20">
                    <i className="ri-arrow-down-circle-fill text-emerald-300 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">+¥{totalIn.toFixed(2)}</p>
                    <p className="text-white/40 text-xs">总收入</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-rose-400/20">
                    <i className="ri-arrow-up-circle-fill text-rose-300 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">-¥{totalOut.toFixed(2)}</p>
                    <p className="text-white/40 text-xs">总支出</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-yellow-400/20">
                    <i className="ri-percent-fill text-yellow-300 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">¥{Number(activeAccount.total_interest).toFixed(2)}</p>
                    <p className="text-white/40 text-xs">累计利息</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Interest Info */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100 rounded-2xl p-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400 flex-shrink-0">
                  <i className="ri-bank-fill text-white text-base"></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-bold text-amber-700 text-sm">按月结息</span>
                    <span className="text-lg font-black text-amber-600">{interestRate}% 年化</span>
                    <span className="text-xs text-amber-500">≈ {(interestRate / 12).toFixed(2)}% 月利率</span>
                  </div>
                  <p className="text-xs text-amber-600">每月1日根据上月1日存款余额计算利息并结入账户</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-amber-500 mb-0.5">预计下月利息</p>
                  <p className="text-lg font-black text-amber-600">¥{nextMonthInterest.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Quick Operations */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              {[
                { icon: 'ri-add-circle-fill', label: '存入', color: 'from-emerald-400 to-teal-500', text: 'text-emerald-700', action: () => setShowDepositModal(true) },
                { icon: 'ri-indeterminate-circle-fill', label: '取出', color: 'from-rose-400 to-rose-500', text: 'text-rose-700', action: () => setShowWithdrawModal(true) },
                { icon: 'ri-coin-fill', label: '充值积分', color: 'from-cyan-400 to-blue-500', text: 'text-cyan-700', action: () => setShowRechargeModal(true) },
                { icon: 'ri-exchange-cny-fill', label: '积分换钱', color: 'from-amber-400 to-orange-500', text: 'text-amber-700', action: () => setShowExchangeModal(true) },
              ].map((action, i) => (
                <button key={i} onClick={action.action}
                  className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl cursor-pointer hover:-translate-y-0.5 transition-all group border border-blue-100">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${action.color} group-hover:scale-110 transition-transform`}>
                    <i className={`${action.icon} text-white text-lg`}></i>
                  </div>
                  <span className={`text-xs font-semibold ${action.text} whitespace-nowrap`}>{action.label}</span>
                </button>
              ))}
            </div>

            {/* Transactions */}
            <div className="bg-white rounded-2xl p-5 border border-blue-100">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className="font-bold text-blue-900 text-sm flex items-center gap-2">
                  <i className="ri-history-fill text-blue-500"></i>交易记录
                  <span className="text-xs font-normal text-gray-400">共{filteredTransactions.length}条</span>
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Time filter */}
                  <div className="flex gap-1 bg-blue-50 p-0.5 rounded-lg border border-blue-100">
                    {([['all','全部'],['7d','近7天'],['30d','近30天'],['90d','近3月']] as const).map(([v, l]) => (
                      <button key={v} onClick={() => setTxTimeFilter(v)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          txTimeFilter === v ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                        }`}>{l}</button>
                    ))}
                  </div>
                  {/* Flow filter */}
                  <div className="flex gap-1 bg-blue-50 p-0.5 rounded-lg border border-blue-100">
                    {([['all','全部'],['in','收入'],['out','支出']] as const).map(([v, l]) => (
                      <button key={v} onClick={() => setTxFlowFilter(v)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                          txFlowFilter === v ? `bg-white shadow-sm ${v === 'in' ? 'text-emerald-600' : v === 'out' ? 'text-rose-500' : 'text-blue-600'}` : 'text-gray-400 hover:text-gray-600'
                        }`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-10 text-gray-400 text-sm">
                  <i className="ri-inbox-line text-3xl block mb-2 text-gray-300"></i>
                  暂无符合条件的交易记录
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTransactions.map(tx => {
                    const typeCfg = txTypeConfig[tx.transaction_type] || txTypeConfig[1];
                    const catCfg = categoryConfig[tx.source_type || ''];
                    return (
                      <div key={tx.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50/40 transition-colors">
                        <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${typeCfg.bg} flex-shrink-0`}>
                          <i className={`${catCfg?.icon || typeCfg.icon} ${catCfg?.color || typeCfg.color} text-base`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-blue-900 text-sm">{tx.remark || typeCfg.label}</div>
                          <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span>{new Date(tx.created_at).toLocaleDateString()}</span>
                            <span className="w-1 h-1 rounded-full bg-gray-200 flex-shrink-0"></span>
                            <span>{catCfg?.label || typeCfg.label}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className={`font-bold text-sm ${Number(tx.amount) >= 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                            {Number(tx.amount) >= 0 ? '+' : ''}¥{Number(tx.amount).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-400">余额 ¥{Number(tx.balance_after).toFixed(2)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Deposit Modal */}
      {showDepositModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowDepositModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[440px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-add-circle-fill text-emerald-500"></i>存入零花钱
              </h3>
              <button onClick={() => setShowDepositModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">来源类型</label>
                <div className="grid grid-cols-3 gap-2">
                  {depositQuickOptions.map(opt => (
                    <button key={opt.key} onClick={() => setDepositForm({ ...depositForm, source_type: opt.key })}
                      className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                        depositForm.source_type === opt.key ? `${opt.color} border-current` : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>
                      <i className={`${opt.icon} text-lg`}></i>
                      <span className="text-xs font-medium whitespace-nowrap">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">金额（元）</label>
                <input type="number" value={depositForm.amount} onChange={e => setDepositForm({ ...depositForm, amount: e.target.value })}
                  placeholder="输入金额" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 text-center text-xl font-bold" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['10', '20', '50', '100', '200'].map(v => (
                  <button key={v} onClick={() => setDepositForm({ ...depositForm, amount: v })}
                    className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 cursor-pointer transition-colors whitespace-nowrap">
                    ¥{v}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">备注</label>
                <input value={depositForm.note} onChange={e => setDepositForm({ ...depositForm, note: e.target.value })}
                  placeholder="例：春节红包、每周零花钱"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <button onClick={handleDeposit}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-emerald-400 to-teal-500 hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                确认存入
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowWithdrawModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[440px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-indeterminate-circle-fill text-rose-500"></i>取出零花钱
              </h3>
              <button onClick={() => setShowWithdrawModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">用途类型</label>
                <div className="grid grid-cols-3 gap-2">
                  {withdrawQuickOptions.map(opt => (
                    <button key={opt.key} onClick={() => setWithdrawForm({ ...withdrawForm, source_type: opt.key })}
                      className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                        withdrawForm.source_type === opt.key ? `${opt.color} border-current` : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}>
                      <i className={`${opt.icon} text-lg`}></i>
                      <span className="text-xs font-medium whitespace-nowrap">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">金额（元）</label>
                <input type="number" value={withdrawForm.amount} onChange={e => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                  placeholder="输入金额" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 text-center text-xl font-bold" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['10', '20', '50', '100', '200'].map(v => (
                  <button key={v} onClick={() => setWithdrawForm({ ...withdrawForm, amount: v })}
                    className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-rose-400 hover:text-rose-600 cursor-pointer transition-colors whitespace-nowrap">
                    ¥{v}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">备注</label>
                <input value={withdrawForm.note} onChange={e => setWithdrawForm({ ...withdrawForm, note: e.target.value })}
                  placeholder="例：买零食、买文具"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
              </div>
              <button onClick={handleWithdraw}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-rose-400 to-rose-500 hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                确认取出
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recharge Points Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowRechargeModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-coin-fill text-cyan-500"></i>充值积分
              </h3>
              <button onClick={() => setShowRechargeModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-cyan-50 rounded-xl p-3 text-center">
                <p className="text-xs text-cyan-600 mb-1">兑换比例</p>
                <p className="text-lg font-black text-cyan-700">1元 = {exchangeRatio}积分</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">充值金额（元）</label>
                <input type="number" value={rechargeForm.amount} onChange={e => setRechargeForm({ amount: e.target.value })}
                  placeholder="输入金额" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 text-center text-xl font-bold" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['10', '20', '50', '100'].map(v => (
                  <button key={v} onClick={() => setRechargeForm({ amount: v })}
                    className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-cyan-400 hover:text-cyan-600 cursor-pointer transition-colors whitespace-nowrap">
                    ¥{v}
                  </button>
                ))}
              </div>
              {rechargeForm.amount && (
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-sm text-blue-700">可获得 <span className="font-black text-lg">{rechargePoints}</span> 积分</p>
                </div>
              )}
              <button onClick={handleRecharge} disabled={!rechargeForm.amount || Number(rechargeForm.amount) <= 0}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-cyan-400 to-blue-500 hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                确认充值
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exchange Points Modal */}
      {showExchangeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowExchangeModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[400px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-exchange-cny-fill text-amber-500"></i>积分换钱
              </h3>
              <button onClick={() => setShowExchangeModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <p className="text-xs text-amber-600 mb-1">兑换比例</p>
                <p className="text-lg font-black text-amber-700">{exchangeRatio}积分 = 1元</p>
                <p className="text-xs text-amber-500 mt-1">当前积分：{activeChild?.total_points || 0}</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">兑换积分</label>
                <input type="number" value={exchangeForm.points} onChange={e => setExchangeForm({ points: e.target.value })}
                  placeholder="输入积分" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 text-center text-xl font-bold" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {['100', '200', '500', '1000'].map(v => (
                  <button key={v} onClick={() => setExchangeForm({ points: v })}
                    className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:border-amber-400 hover:text-amber-600 cursor-pointer transition-colors whitespace-nowrap">
                    {v}分
                  </button>
                ))}
              </div>
              {exchangeForm.points && (
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-sm text-emerald-700">可获得 <span className="font-black text-lg">¥{exchangeRmb}</span></p>
                </div>
              )}
              <button onClick={handleExchange} disabled={!exchangeForm.points || Number(exchangeForm.points) <= 0}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-amber-400 to-orange-500 hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                确认兑换
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interest Rate Modal */}
      {showRateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowRateModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-[360px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <i className="ri-settings-3-fill text-blue-500"></i>利率设置
              </h3>
              <button onClick={() => setShowRateModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">年化利率（%）</label>
                <input type="number" value={rateForm.rate} onChange={e => setRateForm({ rate: e.target.value })} step="0.1" min="0" max="20"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-400 text-center text-xl font-bold" />
              </div>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-600">月利率：{(Number(rateForm.rate) / 12).toFixed(2)}%</p>
                <p className="text-xs text-blue-500 mt-1">每月1日根据上月1日存款余额计算利息</p>
              </div>
              <button onClick={handleSetRate}
                className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                保存设置
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
